/**
 * Normalização e exibição de contexto Click-to-WhatsApp (anúncio Meta).
 *
 * O payload Baileys/Uazapi entrega `externalAdReply` com:
 * - `title`: geralmente o CTA/nome da página ("Fale conosco", "Saiba mais")
 * - `body`: texto completo do criativo (é o que identifica o anúncio)
 * - `sourceType`: "ad" (sem indicar IG ou FB), `mediaType`: 1 = imagem, 2 = vídeo
 */

export type AdReplyPlatform = "instagram" | "facebook" | "meta";

export interface RawAdReply {
  platform?: string | null;
  sourceType?: string | null;
  sourceId?: string | null;
  sourceUrl?: string | null;
  ctwaClid?: string | null;
  title?: string | null;
  body?: string | null;
  adHeadline?: string | null;
  mediaType?: string | number | null;
  mediaTypeRaw?: string | number | null;
  mediaUrl?: string | null;
  thumbnailUrl?: string | null;
}

export interface NormalizedAdReply {
  platform: AdReplyPlatform;
  platformLabel: string;
  /** Melhor identificação do anúncio (título real ou 1ª linha significativa do texto). */
  headline: string | null;
  /** Título original do criativo (CTA/página), quando diferente da headline. */
  title: string | null;
  body: string | null;
  mediaKind: "image" | "video" | null;
  thumbnailUrl: string | null;
  sourceUrl: string | null;
  ctwaClid: string | null;
}

/** Títulos que são apenas CTA e não identificam o anúncio. */
const GENERIC_TITLES = [
  "fale conosco",
  "fale connosco",
  "saiba mais",
  "enviar mensagem",
  "send message",
  "learn more",
  "conversar",
  "chamar no whatsapp",
  "whatsapp",
  "contato",
  "compre agora",
  "shop now",
  "cadastre-se",
  "sign up",
];

export function isGenericAdTitle(title?: string | null): boolean {
  const t = (title || "").trim().toLowerCase();
  if (!t) return true;
  return GENERIC_TITLES.includes(t);
}

/** Primeira linha significativa do texto do anúncio (sem emojis soltos/hashtags). */
export function deriveAdHeadline(body?: string | null): string | null {
  if (!body) return null;
  const lines = String(body).split(/\r?\n/);
  for (const raw of lines) {
    let line = raw.trim();
    if (!line) continue;
    if (line.startsWith("#")) continue;
    // remove emojis/símbolos de decoração nas bordas
    line = line.replace(/^[^\p{L}\p{N}]+/u, "").replace(/[^\p{L}\p{N}%!?.)\]]+$/u, "").trim();
    if (line.length < 3) continue;
    return line.length > 90 ? `${line.slice(0, 90)}…` : line;
  }
  return null;
}

export function normalizeAdMediaKind(
  mediaType?: string | number | null,
): "image" | "video" | null {
  if (mediaType == null) return null;
  if (typeof mediaType === "number") {
    if (mediaType === 1) return "image";
    if (mediaType === 2) return "video";
    return null;
  }
  const t = String(mediaType).toLowerCase();
  if (t.includes("image") || t === "1") return "image";
  if (t.includes("video") || t === "2") return "video";
  return null;
}

export function normalizeAdPlatform(ad: RawAdReply): AdReplyPlatform {
  const declared = String(ad.platform || "").toLowerCase();
  if (declared === "instagram" || declared === "facebook") return declared;
  const sourceType = String(ad.sourceType || "").toLowerCase();
  const sourceUrl = typeof ad.sourceUrl === "string" ? ad.sourceUrl : "";
  if (sourceType.includes("ig") || sourceType.includes("instagram")) return "instagram";
  if (sourceType.includes("fb") || sourceType.includes("facebook")) return "facebook";
  if (/instagram\.com/i.test(sourceUrl)) return "instagram";
  if (/facebook\.com|fb\.com/i.test(sourceUrl)) return "facebook";
  // sourceType "ad" genérico / desconhecido: é sempre anúncio da Meta.
  return "meta";
}

export function adPlatformLabel(platform: AdReplyPlatform): string {
  if (platform === "instagram") return "Instagram";
  if (platform === "facebook") return "Facebook";
  return "Facebook/Instagram";
}

export function normalizeAdReply(raw: unknown): NormalizedAdReply | null {
  if (!raw || typeof raw !== "object") return null;
  const ad = raw as RawAdReply;
  const platform = normalizeAdPlatform(ad);
  const title = ad.title?.trim() || null;
  const body = ad.body?.trim() || null;
  const derived = ad.adHeadline?.trim() || deriveAdHeadline(body);
  const headline = !isGenericAdTitle(title) ? title : derived || title;

  return {
    platform,
    platformLabel: adPlatformLabel(platform),
    headline: headline || null,
    title: title && title !== headline ? title : null,
    body,
    mediaKind: normalizeAdMediaKind(ad.mediaType ?? ad.mediaTypeRaw),
    thumbnailUrl: ad.thumbnailUrl || ad.mediaUrl || null,
    sourceUrl: typeof ad.sourceUrl === "string" && /^https?:\/\//i.test(ad.sourceUrl)
      ? ad.sourceUrl
      : null,
    ctwaClid: ad.ctwaClid || null,
  };
}
